// University of Missouri (Columbia): public college page (#2840), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Mizzou has no current Common Data Set: its University Data, Analytics,
//    and Institutional Research page (udair.missouri.edu) posts editions
//    through 2023-24 only. The first-year counts therefore come from the U.S.
//    Department of Education's IPEDS Admissions survey for Fall 2024,
//    first-time degree-seeking students (College Navigator and the ADM2024
//    complete data file, unit 178396): 24,490 applied, 19,218 admitted,
//    5,953 enrolled. College Navigator shows the rounded 78%.
//  - Mizzou Admissions: Dates & Deadlines, Freshman Admission
//    Requirements (Fall 2027) and Test-Optional Admission (Fall 2027). The
//    deadlines table carries no year; the same page lists Fall 2027 transfer
//    dates, so it is the page for this cycle. Decisions are released on a
//    rolling basis from October 1.
//  - Mizzou Student Financial Aid, Undergraduate Cost of Attendance,
//    2026-27, living in a residence hall. Tuition has three tiers set by
//    major; the table uses Tier 1, the lowest, and the note gives the
//    difference for Tiers 2 and 3. The page lists no transportation item.
//  - College Scorecard, school id 178396 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=178396#admsns',
        read: '2026-10-01',
    },
    dates: {
        label: 'Mizzou Admissions, Dates & Deadlines',
        url: 'https://admissions.missouri.edu/apply/dates-deadlines/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Mizzou Admissions, Freshman Admission Requirements',
        url: 'https://admissions.missouri.edu/apply/freshmen/admission-requirements/',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Mizzou Admissions, Test-Optional Admission',
        url: 'https://admissions.missouri.edu/apply/freshmen/test-optional/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Mizzou Student Financial Aid, Undergraduate Cost of Attendance 2026-27',
        url: 'https://financialaid.missouri.edu/cost-of-attendance/undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?178396-University-of-Missouri-Columbia',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-missouri',
    kbId: 'university_of_missouri',
    name: 'University of Missouri',
    shortName: 'Mizzou',
    aliases: ['Mizzou'],
    location: 'Columbia, MO',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (brand.missouri.edu brand colors); decoration only.
    branding: {
        primary: '#FDB719',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_missouri_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 24490,
        admitted: 19218,
        enrolled: 5953,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Mizzou publishes no first-year acceptance rate by college or major, and it has not posted a Common Data Set since 2023-24, so the counts above come from the federal IPEDS survey.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Mizzou lists no early decision or early action plan. It releases decisions on a rolling basis from October 1, and December 1 is the deadline to apply for priority scholarship consideration.',
        source: 'dates',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Mizzou, and admission is decided for the university. Two things work differently:',
        rows: [
            { school: 'Honors College', requirement: 'A separate application, open from September 15 to February 1, with a December 1 deadline for honors scholarships. You do not need to be admitted to Mizzou first, but your official test scores and transcripts must be on file with Mizzou Admissions.', source: 'dates' },
            { school: 'Journalism, engineering, business and others', requirement: 'Freshman admission does not guarantee admission to professional or upper-division programs. These programs have separate admission criteria, and many majors have competitive or selective admission after the freshman year.', source: 'requirements' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'July 1' },
        rows: [
            { plan: 'Priority scholarship deadline', deadline: 'December 1', decision: 'Rolling, from October 1', binding: false },
            { plan: 'Application deadline', deadline: 'July 1', decision: 'Rolling, from October 1', binding: false },
        ],
        note: 'The application opens August 1. December 1 is also the deadline for general, competitive and departmental scholarships. Mizzou\'s FAFSA deadline is February 1, aid offers go out in mid-March, and National Decision Day is May 1.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Fall 2027 freshman applicants choose a review with or without SAT or ACT scores. With scores, you are eligible for admission if you complete the 17 required high school courses and meet the minimum score for your class rank or core GPA (an ACT 24 or SAT 1160 and above needs a 2.0 core GPA). Without scores, a committee reviews your transcript, a resume or activities list and a short essay. Home-schooled students and students from non-accredited schools must send the ACT or SAT. Mizzou superscores both tests.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testOptional',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (Tier 1)', amounts: [14906, 38068] },
            { item: 'Required fees', amounts: [1256, 1256] },
            { item: 'Housing and food (residence hall)', amounts: [15780, 15780] },
            { item: 'Books and supplies', amounts: [918, 918] },
            { item: 'Personal expenses', amounts: [3466, 3466] },
        ],
        totals: [36326, 59488],
        note: 'For a full-time undergraduate living in a residence hall, fall and spring. Tuition depends on your major: Tier 1 is shown, Tier 2 adds $2,064 a year and Tier 3 adds $4,128, for residents and nonresidents alike. Mizzou lists no transportation estimate.',
        source: 'coa',
        netPrice: {
            average: 21142,
            byIncome: [
                { income: '$0 to $30,000', amount: 15038 },
                { income: '$30,001 to $48,000', amount: 14754 },
                { income: '$48,001 to $75,000', amount: 15464 },
                { income: '$75,001 to $110,000', amount: 18672 },
                { income: 'Over $110,000', amount: 27755 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Mizzou\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
